import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager"
import moment from "moment";

//STATE INITIAL
const INIT_STATE = {
    loading: false,
    driverPerformanceDriverDetails: {}, //information for drivers table
    searchDriverDashboardGadgetData: {},
    drivers: [], // Array to store accumulated drivers
    total: 0,
    nextToken: null,
    filters: {
        openSearch: "", // driverName
        drivers: [],
        dateStart: null,
        dateEnd: null,
        timeFrameOptionSelected: null,
        noDriver: false,
        tags: [],
        events: []
    },
    error: null
};

const route = 'src/modules/driverPerformance/views/driver/redux/slice.js';

const searchDriverPerformanceDrivers = createAsyncThunk('src/modules/driverPerformance/views/driver/redux/slice/searchDriverPerformanceDrivers', async (payload, { rejectWithValue, dispatch }) => {
    try {
        const { conditions } = payload || {};

        let dateRange = conditions?.find((item) => item.range);
        let pagination = conditions?.find((item) => item.page);
        let tags = conditions?.find((item) => item.tagIds);
        let events = conditions?.find((item) => item.events);
        let drivers = conditions?.find((item) => item.drivers);        
        let openSearch = conditions?.find((item) => item.openSearch);
        let nextTokenParam = conditions?.find((item) => item.nextToken);
        let riskScore = conditions?.find((item) => item.riskScore);

        let params = {
            startDate: dateRange?.range?.GTE || moment().subtract(1, 'month').startOf('day').toISOString(), 
            endDate: dateRange?.range?.LTE || moment().endOf('day').toISOString(), 
            driverIds: drivers?.drivers || [],
            eventNames: events?.events?.length > 0 ? [...events.events] : [],
            driverName: openSearch?.openSearch || "",
            tagIds: tags?.tagIds?.length > 0 && !tags?.tagIds?.includes(-99) ? tags?.tagIds : [],
            groupBy: "driver",
            riskScore: riskScore?.riskScore,
            limit: 100,
            nextToken: nextTokenParam?.nextToken || ""
        }

        const response = await clientQuery(
        searchDriverPerformanceDrivers.query,
        { ...params },
        endpoints.GRAPHQL_GENERAL_V2
        );

        if (!response?.data) {
            throw 'searchDriverPerformanceDrivers : No data found';
        }

        return response?.data;
    } catch (exc) {
        ExceptionManager(exc, route, 'searchDriverPerformanceDrivers');
        return rejectWithValue(exc)
    }
});

searchDriverPerformanceDrivers.query = `
    query searchDriverPerformanceDrivers(
        $startDate: AWSDateTime!
        $endDate: AWSDateTime!
        $driverIds: [Int]
        $eventNames: [String]
        $driverName: String
        $groupBy: enumDriverPerformanceGroupBy
        $sort: DriverPerformanceSortInput
        $tagIds: [Int]
        $limit: Int
        $offset: Int
        $nextToken: String
        $riskScore: Int
    ) {
            data: searchDriverPerformanceDrivers(
            startDate: $startDate, 
            endDate: $endDate, 
            driverIds: $driverIds, 
            eventNames: $eventNames, 
            driverName: $driverName, 
            groupBy: $groupBy, 
            sort: $sort, 
            tagIds: $tagIds, 
            limit: $limit, 
            offset: $offset,
            nextToken: $nextToken
            riskScore: $riskScore
        ) 
        {
            total
            items {
                driverId
                driverName
                totalEvents
                totalDistance
                avgEvents
                riskEvents
                riskScore
                driverTags
                driverTagsNames
                lastCase
                lastCaseStatusId
            }
            nextToken
        }
    }
`;

export { searchDriverPerformanceDrivers };

export const searchDashboardGadgetData = createAsyncThunk(
  'dashboardGadgetData/searchDashboardGadgetData',
  async (
    { data },
    { rejectWithValue }
  ) => {
    try {
      
        /* enum enumDashboardGadgetKey{
            EventHighlights
            RiskEventDistribution
            RiskEventsByDriver
            RiskEventsByTag
        } */
        
        let params = {
            gadgetKey: "EventHighlights",
            timeFrame: {
                fields: ["date"],
                GTE: moment().subtract(1, 'month').startOf('day').toISOString(),
                //GTE: moment().subtract(2, 'days').startOf('day').toISOString(), 
                LTE: moment().endOf('day').toISOString()
            },
            conditions: [],
            sort: [{ field: "tagId", order: "ASC" }], // Changed 'direction' to 'order'
            limit: 10,
            offset: 0,
            nextToken: null
        };

      const response = await clientQuery(
        `
        query searchDashboardGadgetData(
          $gadgetKey: enumDashboardGadgetKey!
          $timeFrame: RangeInput
          $conditions: [GadgetDataSearchInput]
          $sort: [GadgetDataSortInput]
          $limit: Int
          $offset: Int
          $nextToken: String
        ) {
          data: searchDashboardGadgetData(
            gadgetKey: $gadgetKey,
            timeFrame: $timeFrame,
            conditions: $conditions,
            sort: $sort,
            limit: $limit,
            offset: $offset,
            nextToken: $nextToken
          ) {
            items
            total
            nextToken
          }
        }
        `,
        params,
        endpoints.GRAPHQL_GENERAL_V2
      );

      
      if (!response?.data) {
        throw 'searchDashboardGadgetData: No data found';
      }

      return response.data;
    } catch (exc) {
      ExceptionManager(exc, 'src/modules/driverPerformance/views/driver/redux/slice.js', 'searchDashboardGadgetData');
      return rejectWithValue(exc);
    }
  }
);


//Slice
const DashboardDriverPerformanceSliceRedux = createSlice({
    name: route,
    initialState: INIT_STATE,
    reducers: {
        reset: (state, action) => {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        },
        setDriverFilters: (state, action) => {
            return {
                ...state,
                filters: {
                    driverName: action.payload?.driverName,
                    drivers: action.payload?.drivers,
                    dateStart: action.payload?.dateStart,
                    dateEnd: action.payload?.dateEnd,
                    timeFrameOptionSelected: action.payload?.timeFrameOptionSelected,
                    noDriver: action.payload?.noDriver,
                    tags: action.payload?.tags || [],
                    events: action.payload?.events || []
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(searchDriverPerformanceDrivers.pending, (state, action) => {
            const { conditions } = action.meta.arg || {}
            let pagination = conditions?.find((item) => item.page);

            if (pagination?.page === 1) {
                state.drivers = []
                state.nextToken = null
                state.driverPerformanceDriverDetails = null
            }

            state.loading = true;
        })
        builder.addCase(searchDriverPerformanceDrivers.fulfilled, (state, action) => {
            const newItems = action.payload?.items || [];
            const nextToken = action.payload?.nextToken || null;
            const total = action.payload?.total || 0;

            state.drivers = [...state.drivers, ...newItems]
            state.nextToken = nextToken
            state.total = total
            state.driverPerformanceDriverDetails = action.payload;
            state.loading = false;
        })
        builder.addCase(searchDriverPerformanceDrivers.rejected, (state, action) => {
            state.loading = false;
            state.driverPerformanceDriverDetails = null;
            state.error = action.payload;
        })


        builder.addCase(searchDashboardGadgetData.pending, (state, action) => {
            state.searchDriverDashboardGadgetData = null;
            state.loading = true;
        })
        builder.addCase(searchDashboardGadgetData.fulfilled, (state, action) => {
            state.loading = false;
            state.searchDriverDashboardGadgetData = action.payload;
        })
        builder.addCase(searchDashboardGadgetData.rejected, (state, action) => {
            state.loading = false;
            state.searchDriverDashboardGadgetData = null;
            state.error = action.payload;
        })

        
    },
})

// Extract and export the action creators object and the reducer
export const { actions, reducer } = DashboardDriverPerformanceSliceRedux;

// Extract and export each action creator by name
export const { reset, setDriverFilters } = actions;

//Simple async action

// Export the reducer, either as a default or named export
export default reducer;